/**
 * Admin Template Store - Zustand State Management
 *
 * Manages category template state for administrators.
 *
 * @validates Requirements 4.1, 4.2, 4.3, 4.4, 4.5, 4.6
 */

import { create } from 'zustand';
import {
  CategoryTemplate,
  CategoryTemplateTreeNode,
  CreateCategoryTemplateRequest,
  UpdateCategoryTemplateRequest,
  BroadcastResult,
} from '@budgira/shared';
import { adminTemplateService } from '../services/admin-template.service';
import i18n from '../i18n';

interface AdminTemplateState {
  templates: CategoryTemplateTreeNode[];
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTemplates: () => Promise<void>;
  createTemplate: (data: CreateCategoryTemplateRequest) => Promise<CategoryTemplate | null>;
  updateTemplate: (id: string, data: UpdateCategoryTemplateRequest) => Promise<CategoryTemplate | null>;
  deleteTemplate: (id: string, recursive?: boolean) => Promise<boolean>;
  estimateBroadcast: (id: string) => Promise<number>;
  broadcastTemplate: (id: string) => Promise<BroadcastResult | null>;
  clearError: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeTemplateError(err: unknown): string {
  // Extract error code from API response
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (err && typeof err === 'object') {
    // Axios error structure
    const axiosError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }

  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    TEMPLATE_NOT_FOUND: 'admin.templates.errors.notFound',
    PARENT_NOT_FOUND: 'admin.templates.errors.parentNotFound',
    VALIDATION_ERROR: 'admin.templates.errors.validation',
    HIERARCHY_LIMIT_EXCEEDED: 'admin.templates.errors.hierarchyLimit',
    HAS_CHILD_TEMPLATES: 'admin.templates.errors.hasChildren',
    BASE_TYPE_REQUIRED: 'admin.templates.errors.baseTypeRequired',
    BROADCAST_FAILED: 'admin.templates.errors.broadcastFailed',
  };

  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }

  // Fallback to backend message or generic error
  return errorMessage || String(i18n.t('admin.templates.errors.generic'));
}

export const useAdminTemplateStore = create<AdminTemplateState>((set, get) => ({
  templates: [],
  isLoading: false,
  error: null,

  /**
   * Fetch all category templates as a tree structure
   * @validates Requirement 4.1
   */
  fetchTemplates: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminTemplateService.getTemplates();
      if (response.success && response.data) {
        set({ templates: response.data, isLoading: false });
      } else {
        set({
          error: response.error?.message || String(i18n.t('admin.templates.errors.generic')),
          isLoading: false,
        });
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
    }
  },

  /**
   * Create a new category template
   * @validates Requirement 4.2
   */
  createTemplate: async (data: CreateCategoryTemplateRequest): Promise<CategoryTemplate | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminTemplateService.createTemplate(data);
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchTemplates();
        return response.data;
      } else {
        set({
          error: response.error?.message || String(i18n.t('admin.templates.errors.generic')),
          isLoading: false,
        });
        return null;
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  /**
   * Update an existing category template
   * @validates Requirement 4.2
   */
  updateTemplate: async (
    id: string,
    data: UpdateCategoryTemplateRequest
  ): Promise<CategoryTemplate | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminTemplateService.updateTemplate(id, data);
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchTemplates();
        return response.data;
      } else {
        set({
          error: response.error?.message || String(i18n.t('admin.templates.errors.generic')),
          isLoading: false,
        });
        return null;
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  /**
   * Delete a category template with optional recursive deletion
   * @validates Requirement 4.2
   */
  deleteTemplate: async (id: string, recursive?: boolean): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminTemplateService.deleteTemplate(id, recursive);
      if (response.success) {
        // Refresh the tree to get updated structure
        await get().fetchTemplates();
        return true;
      } else {
        set({
          error: response.error?.message || String(i18n.t('admin.templates.errors.generic')),
          isLoading: false,
        });
        return false;
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  /**
   * Estimate the number of users that will receive a broadcast
   * @validates Requirement 4.5
   */
  estimateBroadcast: async (id: string): Promise<number> => {
    try {
      const response = await adminTemplateService.estimateBroadcast(id);
      if (response.success && response.data) {
        return response.data.count;
      }
      return 0;
    } catch {
      return 0;
    }
  },

  /**
   * Broadcast a template to all users who don't have it
   * @validates Requirement 4.3, 4.4, 4.6
   */
  broadcastTemplate: async (id: string): Promise<BroadcastResult | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await adminTemplateService.broadcastTemplate(id);
      if (response.success && response.data) {
        set({ isLoading: false });
        return response.data;
      } else {
        set({
          error: response.error?.message || String(i18n.t('admin.templates.errors.generic')),
          isLoading: false,
        });
        return null;
      }
    } catch (err) {
      const message = localizeTemplateError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  /**
   * Clear the current error state
   */
  clearError: () => set({ error: null }),
}));

export default useAdminTemplateStore;
